import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { locations } from '../data/sbbData';
import { MapPin, Phone, ExternalLink, Search, Filter } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import PageHero from '../components/PageHero';

// Leaflet marker default icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function OperationalLocations() {
  const { lang, t } = useLanguage();
  const [selectedType, setSelectedType] = useState('all');
  const [searchCity, setSearchCity] = useState('');

  const filteredLocations = locations.filter((loc) => {
    const matchesType = selectedType === 'all' || loc.type === selectedType;
    const matchesCity = loc.name.toLowerCase().includes(searchCity.toLowerCase()) || 
                        loc.city.toLowerCase().includes(searchCity.toLowerCase()) ||
                        loc.province.toLowerCase().includes(searchCity.toLowerCase());
    return matchesType && matchesCity;
  });

  return (
    <div className="operational-locations-page">
      {/* Page Hero */}
      <PageHero eyebrow="Jaringan Fasilitas Nasional" title={t('nav_locations')} subtitle="Jelajahi lokasi batching plant, tambang agregat, dan kantor operasional Solusi Bangun Beton di seluruh Indonesia." image="/reference-images/aggregates.webp" />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('nav_locations')}</span></li>
          </ul>
        </div>
      </div>

      {/* Filter & Interactive Map Section */}
      <section className="section">
        <div className="container">
          {/* Controls Bar */}
          <div className="locations-filter-bar">
            <div className="filter-chips">
              <button 
                className={`chip-btn ${selectedType === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedType('all')}
              >
                {t('filter_all')} Fasilitas ({locations.length})
              </button>
              <button 
                className={`chip-btn ${selectedType === 'Batching Plant' ? 'active' : ''}`}
                onClick={() => setSelectedType('Batching Plant')}
              >
                Batching Plant
              </button>
              <button 
                className={`chip-btn ${selectedType === 'Tambang Agregat' ? 'active' : ''}`}
                onClick={() => setSelectedType('Tambang Agregat')}
              >
                Tambang Agregat (Quarry)
              </button>
              <button 
                className={`chip-btn ${selectedType === 'Kantor Pusat' ? 'active' : ''}`}
                onClick={() => setSelectedType('Kantor Pusat')}
              >
                Kantor Pusat
              </button>
            </div>

            <div className="search-input-box">
              <Search size={18} className="search-icon" />
              <input 
                type="text" 
                placeholder="Cari nama kota / provinsi..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
              />
            </div>
          </div>

          {/* Interactive Leaflet Map Container */}
          <div className="map-view-card">
            <MapContainer 
              center={[-6.2, 106.8]} 
              zoom={8} 
              scrollWheelZoom={false}
              style={{ height: '480px', width: '100%', borderRadius: '12px' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filteredLocations.map((loc) => (
                <Marker key={loc.id} position={[loc.lat, loc.lng]}>
                  <Popup>
                    <div className="map-popup-content">
                      <strong className="popup-title">{loc.name}</strong>
                      <div className="popup-tag">{loc.type}</div>
                      <p className="popup-address">{loc.address}</p>
                      <p className="popup-phone"><strong>Telp:</strong> {loc.phone}</p>
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="popup-link"
                      >
                        Buka di Google Maps ↗
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Location Cards List View (Fallback / Detail) */}
          <div className="location-list-header">
            <h3 className="section-title" style={{ fontSize: '24px' }}>Daftar Lokasi Terdaftar ({filteredLocations.length})</h3>
          </div>

          {filteredLocations.length > 0 ? (
            <div className="grid-3">
              {filteredLocations.map((loc) => (
                <div key={loc.id} className="sbb-card location-card">
                  <div className="sbb-card-body">
                    <span className="sbb-card-tag">{loc.type}</span>
                    <h3 className="sbb-card-title">{loc.name}</h3>
                    
                    <div className="loc-detail-row">
                      <MapPin size={16} className="icon-red" />
                      <span>{loc.address}, {loc.city}, {loc.province}</span>
                    </div>

                    <div className="loc-detail-row">
                      <Phone size={16} className="icon-red" />
                      <span>{loc.phone}</span>
                    </div>

                    <div className="loc-capacity-badge">
                      Kapasitas: {loc.capacity}
                    </div>

                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`}
                      target="_blank" 
                      rel="noreferrer"
                      className="btn btn-outline-red btn-sm btn-full"
                      style={{ marginTop: '16px' }}
                    >
                      <span>Petunjuk Arah Google Maps</span>
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state-box text-center">
              <p className="no-data-text">{t('no_results')}</p>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .locations-filter-bar {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .map-view-card {
          margin-bottom: 48px;
          border: 1px solid var(--color-border);
          border-radius: var(--border-radius-md);
          overflow: hidden;
          box-shadow: var(--shadow-card);
        }

        .map-popup-content {
          font-family: var(--font-main);
          font-size: 13px;
        }

        .popup-title {
          font-size: 14px;
          color: var(--color-ink);
        }

        .popup-tag {
          font-size: 11px;
          font-weight: 700;
          color: var(--color-brand-primary);
          margin-bottom: 4px;
        }

        .popup-address {
          margin: 4px 0;
          color: var(--color-text-muted);
        }

        .popup-link {
          display: inline-block;
          margin-top: 6px;
          color: var(--color-brand-primary);
          font-weight: 600;
        }

        .location-list-header {
          margin-bottom: 24px;
        }

        .loc-detail-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 13px;
          color: var(--color-text);
          margin-bottom: 8px;
        }

        .loc-capacity-badge {
          background-color: var(--color-surface-subtle);
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          color: var(--color-ink);
          margin-top: 8px;
          display: inline-block;
        }
      `}</style>
    </div>
  );
}
